<div class="flex flex-col gap-6">

    <header>
        <h2 class="text-xl font-semibold text-p-text m-0">
            {{ 'bookingV2.berth.title' | translate }}
        </h2>
        <p class="text-sm text-p-text-muted m-0 mt-1">
            {{ 'bookingV2.berth.subtitle' | translate }}
        </p>
    </header>

    <!-- Active selection: boat slot + berth slot. Each renders its current
         selection or an empty dashed placeholder, so the user always sees what
         the booking still needs. Berth rows are expandable (date-step range). -->

    <booking-v2-selection-summary
        [searchDimensions]="searchDimsBesideBoat()"
        (changeBoatRequested)="onChangeBoatFromSummary()" />

    <!-- Non-blocking warning: a selected berth does not fit the boat by dimensions.
         Selection stays allowed (deliberate override via the unfitting toggle). -->
    @if (hasUnfitSelection()) {
        <div
            class="flex items-start gap-3 rounded-lg border px-4 py-3 border-amber-400 bg-amber-50"
            role="alert">
            <span class="material-icons flex-shrink-0 text-amber-600" aria-hidden="true">warning</span>
            <p class="text-sm font-medium text-amber-800 m-0">
                {{ 'bookingV2.berth.list.unfitSelectionWarning' | translate }}
            </p>
        </div>
    }

    <!-- Mode selection grid -->
    <div
        class="grid grid-cols-2 md:grid-cols-4 gap-3"
        role="group"
        [attr.aria-label]="('bookingV2.berth.modeGridAria' | translate)">
        @for (option of modeOptions; track option.mode) {
            <button
                type="button"
                class="group flex flex-col items-center text-center gap-2 p-4 rounded-lg border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                       bg-p-content-bg border-p-border text-p-text
                       hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]
                       aria-pressed:border-p-primary aria-pressed:bg-[color-mix(in_srgb,var(--p-primary-color)_8%,transparent)]"
                [attr.aria-pressed]="selectionMode() === option.mode"
                (click)="selectMode(option.mode)">
                <span
                    class="material-icons text-3xl transition-colors text-p-text-muted
                           group-hover:text-p-primary group-aria-pressed:text-p-primary"
                    aria-hidden="true">{{ option.icon }}</span>
                <span class="font-semibold text-sm text-p-text">
                    {{ option.titleKey | translate }}
                </span>
                <span class="text-xs text-p-text-muted">
                    {{ option.descKey | translate }}
                </span>
            </button>
        }
    </div>

    <!-- Mode panels -->
    @switch (selectionMode()) {

        @case ('properties') {
            <section class="bg-p-content-bg border border-p-border rounded-lg p-5 flex flex-col gap-4">

                <div>
                    <h3 class="text-base font-semibold text-p-text m-0">
                        {{ 'bookingV2.berth.properties.title' | translate }}
                    </h3>
                    <p class="text-sm text-p-text-muted m-0 mt-0.5">
                        {{ 'bookingV2.berth.properties.subtitle' | translate }}
                    </p>
                </div>

                <div class="flex flex-wrap items-end gap-3">
                    <div class="flex-1 min-w-[6rem] flex flex-col gap-1">
                        <div class="flex items-center gap-1">
                            <label for="prop-length" class="text-sm font-medium text-p-text">
                                {{ 'bookingV2.berth.properties.lengthLabel' | translate }} ({{ 'bookingV2.berth.properties.unit' | translate }})
                            </label>
                            <span
                                class="material-icons text-base text-p-text-muted cursor-help"
                                [pTooltip]="('bookingV2.berth.properties.lengthTooltip' | translate)"
                                tooltipPosition="top">help_outline</span>
                        </div>
                        <input
                            pInputText
                            id="prop-length"
                            type="number"
                            min="0"
                            max="99"
                            step="0.1"
                            class="w-full"
                            [ngModel]="propLength()"
                            (ngModelChange)="onPropFieldChange('length', $event)" />
                    </div>
                    <div class="flex-1 min-w-[6rem] flex flex-col gap-1">
                        <label for="prop-width" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.berth.properties.widthLabel' | translate }} ({{ 'bookingV2.berth.properties.unit' | translate }})
                        </label>
                        <input
                            pInputText
                            id="prop-width"
                            type="number"
                            min="0"
                            max="99"
                            step="0.1"
                            class="w-full"
                            [ngModel]="propWidth()"
                            (ngModelChange)="onPropFieldChange('width', $event)" />
                    </div>
                    <div class="flex-1 min-w-[6rem] flex flex-col gap-1">
                        <label for="prop-depth" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.berth.properties.depthLabel' | translate }} ({{ 'bookingV2.berth.properties.unit' | translate }})
                        </label>
                        <input
                            pInputText
                            id="prop-depth"
                            type="number"
                            min="0"
                            max="99"
                            step="0.1"
                            class="w-full"
                            [ngModel]="propDepth()"
                            (ngModelChange)="onPropFieldChange('depth', $event)" />
                    </div>

                    <u2b-icon-button
                        icon="search"
                        [disabled]="!propertiesValid()"
                        [ariaLabel]="('bookingV2.berth.properties.apply' | translate)"
                        [tooltip]="('bookingV2.berth.properties.apply' | translate)"
                        tooltipPosition="top"
                        (clicked)="applyPropertiesMode()" />
                </div>

                @if (!propertiesValid()) {
                    <p class="text-xs text-p-text-muted m-0">
                        {{ 'bookingV2.berth.properties.validationHint' | translate }}
                    </p>
                }
            </section>
        }

        @case ('existingBoat') {
            <section class="bg-p-content-bg border border-p-border rounded-lg p-5 flex flex-col gap-4">

                    <div class="flex items-center justify-between gap-3">
                        <div>
                            <h3 class="text-base font-semibold text-p-text m-0">
                                {{ 'bookingV2.berth.existingBoat.title' | translate }}
                            </h3>
                            <p class="text-sm text-p-text-muted m-0 mt-0.5">
                                {{ 'bookingV2.berth.existingBoat.subtitle' | translate }}
                            </p>
                        </div>
                        <button
                            type="button"
                            class="text-p-text-muted hover:text-p-text rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                            [attr.aria-label]="('bookingV2.berth.existingBoat.closeAria' | translate)"
                            (click)="closeExistingBoatPanel()">
                            <span class="material-icons" aria-hidden="true">close</span>
                        </button>
                    </div>

                    <div class="flex gap-2 items-center w-full">
                        <p-iconfield iconPosition="left" styleClass="flex-1 min-w-0">
                            <p-inputicon>
                                <span class="material-icons text-p-text-muted">search</span>
                            </p-inputicon>
                            <input
                                pInputText
                                type="search"
                                class="w-full"
                                [ngModel]="boatQuery()"
                                (ngModelChange)="onBoatQueryChange($event)"
                                [placeholder]="('bookingV2.berth.existingBoat.searchPlaceholder' | translate)"
                                [attr.aria-label]="('bookingV2.berth.existingBoat.searchPlaceholder' | translate)" />
                        </p-iconfield>
                        <u2b-icon-button
                            icon="qr_code_scanner"
                            [tooltip]="('bookingV2.berth.existingBoat.scanQrAria' | translate)"
                            tooltipPosition="top"
                            [ariaLabel]="('bookingV2.berth.existingBoat.scanQrAria' | translate)"
                            [disabled]="importingBoatFromQr() || loadingBoatSearch()"
                            (clicked)="scanBoatFromQr()" />
                    </div>

                    <p class="text-xs text-p-text-muted m-0">
                        {{ 'bookingV2.berth.existingBoat.up2boatHint' | translate }}
                    </p>

                    @if (boatQueryEmpty()) {
                        <p class="text-xs text-p-text-muted m-0">
                            {{ 'bookingV2.berth.existingBoat.minLengthHint' | translate }}
                        </p>
                    }

                    @if (loadingBoatSearch()) {
                        <div class="flex items-center justify-center gap-3 py-6 text-p-text-muted" role="status" aria-live="polite">
                            <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                            <span class="text-sm">{{ 'bookingV2.berth.existingBoat.loading' | translate }}</span>
                        </div>
                    }

                    @if (boatShowNoResults()) {
                        <div class="text-center py-6 text-p-text-muted">
                            <span class="material-icons text-3xl opacity-60 mb-1 block" aria-hidden="true">search_off</span>
                            <p class="text-sm m-0">{{ 'bookingV2.berth.existingBoat.noResults' | translate }}</p>
                        </div>
                    }

                    @if (!loadingBoatSearch() && boatSearchHasResults()) {
                        <div class="flex flex-col gap-4 max-h-[420px] overflow-y-auto pr-1">

                            <!-- Boats -->
                            @if (boatSearchResult().boats.length > 0) {
                                <div>
                                    <div class="flex items-center gap-2 mb-2">
                                        <span class="material-icons text-base text-p-text-muted" aria-hidden="true">directions_boat</span>
                                        <span class="text-xs font-medium text-p-text-muted uppercase tracking-wide">
                                            {{ 'bookingV2.berth.existingBoat.sections.boats' | translate }}
                                        </span>
                                    </div>
                                    <div class="flex flex-col gap-1.5" role="listbox" [attr.aria-label]="('bookingV2.berth.existingBoat.sections.boats' | translate)">
                                        @for (boat of boatSearchResult().boats; track boat.id) {
                                            <button
                                                type="button"
                                                role="option"
                                                class="w-full p-3 border rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                                                       bg-p-content-bg border-p-border
                                                       hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]
                                                       aria-selected:border-p-primary
                                                       aria-selected:bg-[color-mix(in_srgb,var(--p-primary-color)_10%,transparent)]
                                                       aria-selected:shadow-[0_1px_3px_color-mix(in_srgb,var(--p-primary-color)_15%,transparent)]"
                                                [attr.aria-selected]="isBoatSelected(boat)"
                                                (click)="selectBoat(boat)">
                                                <div class="flex items-center justify-between gap-3">
                                                    <div class="flex-1 min-w-0">
                                                        <div class="font-semibold text-p-text">
                                                            {{ boat.name || ('bookingV2.berth.summary.unnamedBoat' | translate) }}
                                                            @if (boat.licensePlate) {
                                                                <span class="text-p-text-muted font-normal ml-1.5">({{ boat.licensePlate }})</span>
                                                            }
                                                        </div>
                                                        <div class="text-sm text-p-text-muted mt-0.5">
                                                            <booking-v2-dimensions [length]="boat.length" [width]="boat.width" [depth]="boat.depth" />
                                                        </div>
                                                        @if (boat.ownerLabel) {
                                                            <div class="flex items-center gap-1 mt-1 text-xs text-p-text-muted">
                                                                <span class="material-icons text-sm" aria-hidden="true">person</span>
                                                                {{ 'bookingV2.berth.summary.ownerLabel' | translate }}: {{ boat.ownerLabel }}
                                                            </div>
                                                        }
                                                    </div>
                                                    <span class="material-icons flex-shrink-0" [class.text-p-primary]="isBoatSelected(boat)" [class.text-gray-300]="!isBoatSelected(boat)" aria-hidden="true">
                                                        {{ isBoatSelected(boat) ? 'check_box' : 'check_box_outline_blank' }}
                                                    </span>
                                                </div>
                                            </button>
                                        }
                                    </div>
                                </div>
                            }

                            <!-- Owners -->
                            @if (boatSearchResult().owners.length > 0) {
                                <div>
                                    <div class="flex items-center gap-2 mb-2">
                                        <span class="material-icons text-base text-p-text-muted" aria-hidden="true">person</span>
                                        <span class="text-xs font-medium text-p-text-muted uppercase tracking-wide">
                                            {{ 'bookingV2.berth.existingBoat.sections.owners' | translate }}
                                        </span>
                                    </div>
                                    <div class="flex flex-col gap-1.5" role="list">
                                        @for (owner of boatSearchResult().owners; track ownerKey(owner)) {
                                            <div role="listitem">
                                                @if (owner.boats.length === 1) {
                                                    <button
                                                        type="button"
                                                        class="w-full p-3 border rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                                                               bg-p-content-bg border-p-border
                                                               hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]
                                                               aria-selected:border-p-primary
                                                               aria-selected:bg-[color-mix(in_srgb,var(--p-primary-color)_10%,transparent)]
                                                               aria-selected:shadow-[0_1px_3px_color-mix(in_srgb,var(--p-primary-color)_15%,transparent)]"
                                                        [attr.aria-selected]="isBoatSelected(owner.boats[0])"
                                                        (click)="selectOwnerBoat(owner, owner.boats[0])">
                                                        <div class="flex items-center gap-3">
                                                            <span class="material-icons flex-shrink-0 text-p-text-muted" aria-hidden="true">
                                                                {{ owner.kind === 'company' ? 'business' : 'person' }}
                                                            </span>
                                                            <div class="flex-1 min-w-0">
                                                                <div class="font-semibold text-p-text">{{ owner.fullName }}</div>
                                                                <div class="text-sm text-p-text-muted mt-0.5">
                                                                    {{ 'bookingV2.berth.existingBoat.singleBoat' | translate }}:
                                                                    {{ owner.boats[0].name || ('bookingV2.berth.summary.unnamedBoat' | translate) }}
                                                                    @if (owner.boats[0].licensePlate) {
                                                                        ({{ owner.boats[0].licensePlate }})
                                                                    }
                                                                </div>
                                                            </div>
                                                            <span class="material-icons flex-shrink-0" [class.text-p-primary]="isBoatSelected(owner.boats[0])" [class.text-gray-300]="!isBoatSelected(owner.boats[0])" aria-hidden="true">
                                                                {{ isBoatSelected(owner.boats[0]) ? 'check_box' : 'check_box_outline_blank' }}
                                                            </span>
                                                        </div>
                                                    </button>
                                                } @else {
                                                    <button
                                                        type="button"
                                                        class="w-full p-3 border rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                                                               bg-p-content-bg border-p-border
                                                               hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]"
                                                        [attr.aria-expanded]="isOwnerExpanded(owner)"
                                                        (click)="toggleOwnerExpanded(owner)">
                                                        <div class="flex items-center gap-3">
                                                            <span class="material-icons flex-shrink-0 text-p-text-muted" aria-hidden="true">
                                                                {{ owner.kind === 'company' ? 'business' : 'person' }}
                                                            </span>
                                                            <div class="flex-1 min-w-0">
                                                                <div class="font-semibold text-p-text">{{ owner.fullName }}</div>
                                                                <div class="text-sm text-p-text-muted mt-0.5">
                                                                    {{ owner.boats.length }} {{ 'bookingV2.berth.existingBoat.boatsCount' | translate }}
                                                                </div>
                                                            </div>
                                                            <span
                                                                class="material-icons flex-shrink-0 transition-transform text-p-text-muted"
                                                                [style.transform]="isOwnerExpanded(owner) ? 'rotate(180deg)' : 'rotate(0)'"
                                                                aria-hidden="true">expand_more</span>
                                                        </div>
                                                    </button>

                                                    @if (isOwnerExpanded(owner)) {
                                                        <div class="mt-1.5 ml-4 pl-3 flex flex-col gap-1.5" style="border-left: 2px solid var(--p-primary-color);">
                                                            @for (boat of owner.boats; track boat.id) {
                                                                <button
                                                                    type="button"
                                                                    class="w-full p-2.5 border rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                                                                           bg-p-content-bg border-p-border
                                                                           hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]
                                                                           aria-selected:border-p-primary
                                                                           aria-selected:bg-[color-mix(in_srgb,var(--p-primary-color)_10%,transparent)]
                                                                           aria-selected:shadow-[0_1px_3px_color-mix(in_srgb,var(--p-primary-color)_15%,transparent)]"
                                                                    [attr.aria-selected]="isBoatSelected(boat)"
                                                                    (click)="selectOwnerBoat(owner, boat)">
                                                                    <div class="flex items-center justify-between gap-3">
                                                                        <div class="flex-1 min-w-0">
                                                                            <div class="font-medium text-p-text">
                                                                                {{ boat.name || ('bookingV2.berth.summary.unnamedBoat' | translate) }}
                                                                                @if (boat.licensePlate) {
                                                                                    <span class="text-p-text-muted font-normal ml-1.5">({{ boat.licensePlate }})</span>
                                                                                }
                                                                            </div>
                                                                            <div class="text-xs text-p-text-muted mt-0.5">
                                                                                <booking-v2-dimensions [length]="boat.length" [width]="boat.width" [depth]="boat.depth" />
                                                                            </div>
                                                                        </div>
                                                                        <span class="material-icons flex-shrink-0" [class.text-p-primary]="isBoatSelected(boat)" [class.text-gray-300]="!isBoatSelected(boat)" aria-hidden="true">
                                                                            {{ isBoatSelected(boat) ? 'check_box' : 'check_box_outline_blank' }}
                                                                        </span>
                                                                    </div>
                                                                </button>
                                                            }
                                                        </div>
                                                    }
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>
                            }

                        </div>
                    }
                </section>
        }

        @case ('newBoat') {
            <section class="bg-p-content-bg border border-p-border rounded-lg p-5 flex flex-col gap-4">

                <div class="flex items-start justify-between gap-3">
                    <div>
                        <h3 class="text-base font-semibold text-p-text m-0">
                            {{ 'bookingV2.berth.newBoat.title' | translate }}
                        </h3>
                        <p class="text-sm text-p-text-muted m-0 mt-0.5">
                            {{ 'bookingV2.berth.newBoat.subtitle' | translate }}
                        </p>
                    </div>
                    <button
                        type="button"
                        class="text-p-text-muted hover:text-p-text rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                        [attr.aria-label]="('bookingV2.berth.newBoat.closeAria' | translate)"
                        (click)="selectMode('newBoat')">
                        <span class="material-icons" aria-hidden="true">close</span>
                    </button>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div class="flex flex-col gap-1">
                        <label for="newboat-name" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.berth.newBoat.nameLabel' | translate }}
                        </label>
                        <input
                            pInputText
                            id="newboat-name"
                            type="text"
                            class="w-full"
                            [placeholder]="('bookingV2.berth.newBoat.namePlaceholder' | translate)"
                            [ngModel]="newBoatName()"
                            (ngModelChange)="newBoatName.set($event)" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label for="newboat-license" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.berth.newBoat.licenseLabel' | translate }}
                        </label>
                        <input
                            pInputText
                            id="newboat-license"
                            type="text"
                            class="w-full"
                            [placeholder]="('bookingV2.berth.newBoat.licensePlaceholder' | translate)"
                            [ngModel]="newBoatLicense()"
                            (ngModelChange)="newBoatLicense.set($event)" />
                    </div>
                </div>

                <!-- Optional owner picker (existing person / company) -->
                <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.berth.newBoat.ownerLabel' | translate }}
                    </label>

                    @if (newBoatOwner(); as owner) {
                        <div class="flex items-center gap-3 p-3 border rounded-lg border-p-border bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]">
                            <span class="material-icons flex-shrink-0 text-p-text-muted" aria-hidden="true">
                                {{ owner.kind === 'company' ? 'business' : 'person' }}
                            </span>
                            <div class="flex-1 min-w-0">
                                <div class="font-medium text-p-text truncate">{{ owner.fullName }}</div>
                            </div>
                            <button
                                type="button"
                                class="text-p-text-muted hover:text-p-text rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                                [attr.aria-label]="('bookingV2.berth.newBoat.ownerRemoveAria' | translate)"
                                (click)="clearNewBoatOwner()">
                                <span class="material-icons" aria-hidden="true">close</span>
                            </button>
                        </div>
                    } @else {
                        <div class="flex gap-2 items-center w-full">
                            <p-iconfield iconPosition="left" styleClass="flex-1 min-w-0">
                                <p-inputicon>
                                    <span class="material-icons text-p-text-muted">search</span>
                                </p-inputicon>
                                <input
                                    pInputText
                                    type="search"
                                    class="w-full"
                                    [ngModel]="ownerQuery()"
                                    (ngModelChange)="onOwnerQueryChange($event)"
                                    [placeholder]="('bookingV2.berth.newBoat.ownerSearchPlaceholder' | translate)"
                                    [attr.aria-label]="('bookingV2.berth.newBoat.ownerSearchPlaceholder' | translate)" />
                            </p-iconfield>
                            <u2b-icon-button
                                icon="qr_code_scanner"
                                [tooltip]="('bookingV2.berth.existingBoat.scanQrAria' | translate)"
                                tooltipPosition="top"
                                [ariaLabel]="('bookingV2.berth.existingBoat.scanQrAria' | translate)"
                                [disabled]="importingOwnerFromQr() || loadingOwnerSearch()"
                                (clicked)="scanOwnerFromQr()" />
                        </div>

                        <p class="text-xs text-p-text-muted m-0 mt-1">
                            {{ 'bookingV2.berth.existingBoat.up2boatHint' | translate }}
                        </p>

                        @if (!ownerQueryEmpty() && loadingOwnerSearch()) {
                            <div class="flex items-center gap-2 py-2 text-p-text-muted" role="status" aria-live="polite">
                                <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                                <span class="text-sm">{{ 'bookingV2.berth.existingBoat.loading' | translate }}</span>
                            </div>
                        }

                        @if (ownerShowNoResults()) {
                            <p class="text-xs text-p-text-muted m-0 mt-1">
                                {{ 'bookingV2.berth.newBoat.ownerNoResults' | translate }}
                            </p>
                        }

                        @if (!loadingOwnerSearch() && ownerSearchHasResults()) {
                            <div
                                class="flex flex-col gap-1.5 mt-1 max-h-[240px] overflow-y-auto pr-1"
                                role="listbox"
                                [attr.aria-label]="('bookingV2.berth.newBoat.ownerLabel' | translate)">
                                @for (owner of ownerSearchResult(); track ownerKey(owner)) {
                                    <button
                                        type="button"
                                        role="option"
                                        [attr.aria-selected]="false"
                                        class="w-full p-3 border rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                                               bg-p-content-bg border-p-border
                                               hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]"
                                        (click)="selectNewBoatOwner(owner)">
                                        <div class="flex items-center gap-3">
                                            <span class="material-icons flex-shrink-0 text-p-text-muted" aria-hidden="true">
                                                {{ owner.kind === 'company' ? 'business' : 'person' }}
                                            </span>
                                            <div class="flex-1 min-w-0">
                                                <div class="font-medium text-p-text truncate">{{ owner.fullName }}</div>
                                            </div>
                                        </div>
                                    </button>
                                }
                            </div>
                        }
                    }
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div class="flex flex-col gap-1">
                        <div class="flex items-center gap-1">
                            <label for="newboat-length" class="text-sm font-medium text-p-text">
                                {{ 'bookingV2.berth.newBoat.lengthLabel' | translate }} ({{ 'bookingV2.berth.newBoat.unit' | translate }})
                            </label>
                            <span
                                class="material-icons text-base text-p-text-muted cursor-help"
                                [pTooltip]="('bookingV2.berth.newBoat.lengthTooltip' | translate)"
                                tooltipPosition="top">help_outline</span>
                        </div>
                        <input
                            pInputText
                            id="newboat-length"
                            type="number"
                            min="0"
                            max="99"
                            step="0.1"
                            class="w-full"
                            [ngModel]="newBoatLength()"
                            (ngModelChange)="onNewBoatFieldChange('length', $event)" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label for="newboat-width" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.berth.newBoat.widthLabel' | translate }} ({{ 'bookingV2.berth.newBoat.unit' | translate }})
                        </label>
                        <input
                            pInputText
                            id="newboat-width"
                            type="number"
                            min="0"
                            max="99"
                            step="0.1"
                            class="w-full"
                            [ngModel]="newBoatWidth()"
                            (ngModelChange)="onNewBoatFieldChange('width', $event)" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label for="newboat-depth" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.berth.newBoat.depthLabel' | translate }} ({{ 'bookingV2.berth.newBoat.unit' | translate }})
                        </label>
                        <input
                            pInputText
                            id="newboat-depth"
                            type="number"
                            min="0"
                            max="99"
                            step="0.1"
                            class="w-full"
                            [ngModel]="newBoatDepth()"
                            (ngModelChange)="onNewBoatFieldChange('depth', $event)" />
                    </div>
                </div>

                @if (!newBoatValid()) {
                    <p class="text-xs text-p-text-muted m-0">
                        {{ 'bookingV2.berth.newBoat.validationHint' | translate }}
                    </p>
                }

                <div class="flex justify-end">
                    <p-button
                        type="button"
                        [disabled]="!newBoatValid()"
                        (onClick)="applyNewBoatMode()">
                        <span pButtonIcon class="material-icons" style="font-size: 1.125rem">check</span>
                        <span pButtonLabel>{{ 'bookingV2.berth.newBoat.apply' | translate }}</span>
                    </p-button>
                </div>
            </section>
        }
    }

    <!-- Berth list -->
    @if (showBerthList()) {
        <section class="bg-p-content-bg border border-p-border rounded-lg p-5 flex flex-col gap-4">

            <div class="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 class="text-base font-semibold text-p-text m-0">
                        {{ 'bookingV2.berth.list.title' | translate }}
                        @if (!loadingBerths() && !berthLoadError() && filteredBerths().length > 0) {
                            <span class="text-sm font-normal text-p-text-muted ml-1">
                                ({{ fittingBerthCount() }} {{ 'bookingV2.berth.list.itemsLabel' | translate }}@if (shownUnfitCount() > 0) {, {{ 'bookingV2.berth.list.unfitCountSuffix' | translate: { count: shownUnfitCount() } }}})
                            </span>
                        }
                    </h3>
                    @if (!loadingBerths() && !berthLoadError() && filteredBerths().length === 0) {
                        <p class="text-sm text-amber-700 m-0 mt-1">
                            {{ 'bookingV2.berth.list.empty' | translate }}
                            @if (hiddenUnfitCount() > 0) {
                                <button
                                    type="button"
                                    class="font-medium text-p-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary rounded ml-1"
                                    (click)="onShowUnfittingChange(true)">
                                    {{ 'bookingV2.berth.list.revealUnfitting' | translate: { count: hiddenUnfitCount() } }}
                                </button>
                            }
                        </p>
                    }
                </div>

                <div class="flex items-center gap-4 flex-wrap">
                    <!-- Search auto-enables the toggle (6895) but keeps it visible, so the
                         user can still restrict search results to fitting berths. -->
                    @if (hasFitBasis()) {
                        <div class="flex items-center gap-2">
                            <p-checkbox
                                inputId="berth-show-unfitting"
                                [binary]="true"
                                [ngModel]="showUnfitting()"
                                (ngModelChange)="onShowUnfittingChange($event)" />
                            <label
                                for="berth-show-unfitting"
                                class="text-sm font-medium text-p-text cursor-pointer select-none m-0">
                                {{ 'bookingV2.berth.list.showUnfitting' | translate }}
                            </label>
                            <span
                                class="material-icons text-base text-p-text-muted cursor-help"
                                [pTooltip]="((searchDimsBesideBoat() ? 'bookingV2.berth.list.showUnfittingHelpSearch' : 'bookingV2.berth.list.showUnfittingHelp') | translate)"
                                tooltipPosition="top">help_outline</span>
                        </div>
                    }

                    <div class="flex items-center gap-2">
                        <p-checkbox
                            inputId="berth-multi-select"
                            [binary]="true"
                            [ngModel]="multiSelect()"
                            (ngModelChange)="onMultiSelectChange($event)" />
                        <label
                            for="berth-multi-select"
                            class="text-sm font-medium text-p-text cursor-pointer select-none m-0">
                            {{ 'bookingV2.berth.list.multiSelect' | translate }}
                        </label>
                        <span
                            class="material-icons text-base text-p-text-muted cursor-help"
                            [pTooltip]="('bookingV2.berth.list.multiSelectHelp' | translate)"
                            tooltipPosition="top">help_outline</span>
                    </div>
                </div>
            </div>

            <!-- Fender (only with a fit basis: a boat or an applied search) -->
            @if (hasFitBasis()) {
                <div class="flex flex-wrap items-center gap-3 rounded-lg border border-p-border bg-[color-mix(in_srgb,var(--p-primary-color)_4%,transparent)] px-3 py-2">
                    <div class="flex items-center gap-2">
                        <p-checkbox
                            inputId="berth-fender-toggle"
                            [binary]="true"
                            [ngModel]="useFender()"
                            (ngModelChange)="onUseFenderChange($event)" />
                        <label
                            for="berth-fender-toggle"
                            class="text-sm font-medium text-p-text cursor-pointer select-none m-0">
                            {{ 'bookingV2.berth.fender.label' | translate }}
                        </label>
                        <span
                            class="material-icons text-base text-p-text-muted cursor-help"
                            [pTooltip]="('bookingV2.berth.fender.help' | translate)"
                            tooltipPosition="top">help_outline</span>
                    </div>

                    @if (useFender()) {
                        <div class="flex items-center gap-2 ml-auto">
                            <label for="berth-fender-cm" class="text-sm text-p-text-muted whitespace-nowrap m-0">
                                {{ 'bookingV2.berth.fender.cmLabel' | translate }}
                            </label>
                            <input
                                pInputText
                                id="berth-fender-cm"
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                class="w-80"
                                [ngModel]="fenderCm()"
                                (ngModelChange)="onFenderCmChange($event)"
                                [attr.aria-label]="('bookingV2.berth.fender.cmLabel' | translate)" />
                            <span class="text-sm text-p-text-muted">{{ 'bookingV2.berth.fender.cmUnit' | translate }}</span>
                        </div>
                    }
                </div>
            }

            <p-iconfield iconPosition="left" styleClass="w-full">
                <p-inputicon>
                    <span class="material-icons text-p-text-muted">search</span>
                </p-inputicon>
                <input
                    pInputText
                    type="search"
                    class="w-full"
                    [ngModel]="berthSearch()"
                    (ngModelChange)="onSearchChange($event)"
                    [placeholder]="('bookingV2.berth.list.searchPlaceholder' | translate)"
                    [attr.aria-label]="('bookingV2.berth.list.searchPlaceholder' | translate)" />
            </p-iconfield>

            @if (loadingBerths()) {
                <div
                    class="flex items-center justify-center gap-3 py-10 text-p-text-muted"
                    role="status"
                    aria-live="polite">
                    <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                    <span class="text-sm">{{ 'bookingV2.berth.list.loading' | translate }}</span>
                </div>
            }

            @if (berthLoadError() && !loadingBerths()) {
                <div
                    class="flex items-start gap-3 rounded-lg border px-4 py-3"
                    style="border-color: #b91c1c; background: color-mix(in srgb, #b91c1c 6%, transparent);"
                    role="alert">
                    <span class="material-icons flex-shrink-0 mt-0.5" style="color: #b91c1c;">error_outline</span>
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-p-text m-0">
                            {{ berthLoadError() | translate }}
                        </p>
                        <button
                            type="button"
                            class="mt-2 inline-flex items-center gap-1 text-sm font-medium text-p-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary rounded"
                            (click)="retryBerthLoad()">
                            <span class="material-icons text-base" aria-hidden="true">refresh</span>
                            {{ 'bookingV2.berth.list.retry' | translate }}
                        </button>
                    </div>
                </div>
            }

            @if (!loadingBerths() && !berthLoadError() && filteredBerths().length > 0) {
                <div
                    class="flex flex-col gap-1.5 max-h-[420px] overflow-y-auto pr-1"
                    role="listbox"
                    [attr.aria-multiselectable]="multiSelect()"
                    [attr.aria-label]="('bookingV2.berth.list.ariaLabel' | translate)">
                    @for (item of filteredBerths(); track item.berth.id) {
                        @let unfitLabel = item.unfitReason ? ('bookingV2.berth.list.unfit.' + item.unfitReason | translate) : null;
                        @let boatUnfitReasonLabel = item.boatUnfitReason ? ('bookingV2.berth.list.unfit.' + item.boatUnfitReason | translate) : null;
                        <button
                            type="button"
                            role="option"
                            class="w-full p-3 border rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                                   bg-p-content-bg border-p-border
                                   hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]
                                   aria-selected:border-p-primary
                                   aria-selected:bg-[color-mix(in_srgb,var(--p-primary-color)_10%,transparent)]
                                   aria-selected:shadow-[0_1px_3px_color-mix(in_srgb,var(--p-primary-color)_15%,transparent)]"
                            [style.border-color]="(unfitLabel || boatUnfitReasonLabel) && !isBerthSelected(item.berth) ? '#f59e0b' : null"
                            [attr.aria-selected]="isBerthSelected(item.berth)"
                            (click)="toggleBerth(item.berth)">
                            <div class="flex items-center justify-between gap-3">
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2 flex-wrap">
                                        <span class="font-semibold text-p-text">{{ item.berth.handle }}</span>
                                        @if (unfitLabel) {
                                            <span
                                                class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-amber-800 bg-amber-100"
                                                [pTooltip]="unfitLabel"
                                                tooltipPosition="top">
                                                <span class="material-icons text-sm" aria-hidden="true">warning</span>
                                                {{ unfitLabel }}
                                            </span>
                                        }
                                        <!-- The booked boat does not fit while the list is fitted to search
                                             dimensions (7165) — the same verdict as the unfit-selection warning. -->
                                        @if (boatUnfitReasonLabel) {
                                            <span
                                                class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-amber-800 bg-amber-100"
                                                [pTooltip]="('bookingV2.berth.list.unfitForBoatHelp' | translate: { reason: boatUnfitReasonLabel })"
                                                tooltipPosition="top">
                                                <span class="material-icons text-sm" aria-hidden="true">directions_boat</span>
                                                {{ 'bookingV2.berth.list.unfitForBoat' | translate }}
                                            </span>
                                        }
                                    </div>
                                    <div class="text-sm text-p-text-muted mt-0.5">
                                        @if (item.berth.pierHandle) {
                                            {{ item.berth.pierHandle }} ·
                                        }
                                        <booking-v2-dimensions lengthKey="bookingV2.berth.dim.length" [length]="item.berth.length" [width]="item.berth.width" [depth]="item.berth.maxDraft" />
                                    </div>
                                </div>
                                <span class="material-icons flex-shrink-0" [class.text-p-primary]="isBerthSelected(item.berth)" [class.text-gray-300]="!isBerthSelected(item.berth)" aria-hidden="true">
                                    {{ isBerthSelected(item.berth) ? 'check_box' : 'check_box_outline_blank' }}
                                </span>
                            </div>
                        </button>
                    }
                </div>
            }
        </section>
    }

</div>
